import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import React from "react";

import jawreposition from "../assets/photos/jaw-reposition.jpg";
import dentalimplant from "../assets/photos/dental-implant.jpg";
import tmd from "../assets/photos/tmd.jpg";
import oralDevice from "../assets/photos/oral-device.jpg";
import facialAeshetics from "../assets/photos/facial-aesthetics.jpg";
import oralCancer from "../assets/photos/oral-cancer.jpg";
import dentalCare from "../assets/photos/dental-care.jpg";
import routineProcedure from "../assets/photos/routine-procedure.jpg";
import wisdomTooth from "../assets/photos/wisdom-tooth.jpg";
const services = [
  {
    title: "TMD/TMJ Disorders",
    description: "Specialized Centre for TMD and Osteoarthritis of TMJ management using a biopsychosocial approach.",
    image: tmd,
    delay: 0.2,
    procedures: [
      "Arthrocentesis",
      "Tongue tie excision",
      "TMJ arthroscopy",
      "Open joint surgery",
      "Total joint replacement (TJR)",
    ],
  },
  {
    title: "Dental Implants",
    description: "Centre for replacement of lost teeth using dental implants and advanced bone regeneration procedures.",
    image: dentalimplant,
    delay: 0.4,
    procedures: [
      "Dental implant placement",
      "Bone grafting and augmentation",
      "Sinus lift procedures",
      "Full mouth rehabilitation",
    ],
  },
  {
    title: "Management of Sleep Apnoea & Snoring",
    description: "Comprehensive management of obstructive sleep apnoea and snoring.",
    image: oralDevice,
    delay: 0.5,
    procedures: [
      "Tongue tie excision",
      "Bruxism/clenching correction with guards & botulinum toxin",
      "Customized Mandibular Advancement Devices (MAD)",
      "Maxillo-Mandibular Advancement (MMA) procedures",
      "Orofacial myofunctional therapy sessions",
    ],
  },
  {
    title: "Wisdom Tooth Surgery",
    description: "Expert care for the safe and painless removal of third molars (wisdom teeth).",
    image: wisdomTooth,
    delay: 0.4,
    procedures: [
      "Surgical extraction of impacted wisdom teeth",
      "Minimally invasive techniques to reduce trauma",
      "Post-surgical care for quick recovery",
    ],
  },
  {
    title: "Facial Aesthetic Practice",
    description: "Enhancing facial aesthetics with advanced non-surgical treatments.",
    image: facialAeshetics,
    delay: 0.5,
    procedures: [
      "Face recontouring using fillers & PDRN threads",
      "Botulinum toxin for frown & smile lines",
      "Acne scars & pigmentation treatment with chemical peels",
      "PRP & GFC for face and hair",
    ],
  },
  {
    title: "Orthodontic & Jaw Repositioning Surgeries",
    description: "Comprehensive orthodontic care combined with advanced jaw surgery solutions.",
    image: jawreposition,
    delay: 0.6,
    procedures: [
      "Orthodontic alignment",
      "Jaw repositioning procedures",
      "Orthognathic surgery",
      "Bite correction",
    ],
  },
  {
    title: "Dental & Oral Surgical Care for Elderly Patients",
    description: "Safe and specialized care for medically compromised individuals.",
    image: dentalCare,
    delay: 0.2,
    procedures: [
      "Diabetes mellitus",
      "Heart diseases",
      "Kidney disorders",
      "Blood thinners",
      "Cancer",
    ],
  },
  {
    title: "Early Detection of Oral Cancer",
    description: "Expertise in identifying & managing pre-cancerous oral lesions with biopsies.",
    image: oralCancer,
    delay: 0.2,
    procedures: [
      "Ulcers",
      "Red & white lesions",
      "Leukoplakia",
      "Erythroleukoplakia",
    ],
  },
  {
    title: "Routine Dental Procedures",
    description: "Comprehensive dental care for all ages.",
    image: routineProcedure, //update routine procedure
    delay: 0.3, //dummy commit
    procedures: [
      "Teeth whitening",
      "Root canal treatment",
      "Composite fillings",
      "Ceramic crowns & bridges",
      "Veneers & laminates",
      "Removable dentures",
      "Braces & clear aligners for children & adults",
    ],
  },
];

const Services = () => {
  // Function to calculate the maximum number of procedures across all services
  const maxProcedures = Math.max(...services.map(service => service.procedures?.length || 0));
  
  return (
    <Box
      id="services"
      sx={{
        py: 8,
        background: "linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)",
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 6,
              fontWeight: 700,
              color: "primary.main",
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -16,
                left: "50%",
                transform: "translateX(-50%)",
                width: 100,
                height: 4,
                backgroundColor: "primary.main",
                borderRadius: 2,
              },
            }}
          >
            Our Services
          </Typography>
        </motion.div>

        <Grid container spacing={3}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: service.delay }}
                viewport={{ once: true }}
                style={{ height: "100%" }}
              >
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                    },
                    position: "relative",
                  }}
                >
                  {/* Top accent bar */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "4px",
                      backgroundColor: "primary.main",
                      zIndex: 1,
                    }}
                  />
                  
                  {/* Service image */}
                  <CardMedia
                    component="img"
                    height="160"
                    image={service.image}
                    alt={service.title}
                    sx={{
                      objectFit: "cover",
                    }}
                  />
                  
                  {/* Card content */}
                  <CardContent
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    {/* Service title */}
                    <Box
                      sx={{
                        borderLeft: "3px solid",
                        borderColor: "primary.main",
                        pl: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          fontSize: { xs: "1.25rem", md: "1.35rem" },
                          lineHeight: 1.3,
                        }}
                      >
                        {service.title}
                      </Typography>
                    </Box>
                    
                    {/* Service description */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ 
                        mb: 2,
                        fontSize: { xs: "0.875rem", md: "0.9rem" },
                        lineHeight: 1.5,
                      }}
                    >
                      {service.description}
                    </Typography>
                    
                    {/* Key procedures section */}
                    <Box 
                      sx={{ 
                        mt: "auto",
                        pt: 1,
                        borderTop: "1px solid",
                        borderColor: "rgba(0,0,0,0.08)",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "primary.main",
                          fontWeight: 600,
                          mb: 1,
                          fontSize: "0.85rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Key Procedures
                      </Typography>
                      
                      {/* Procedures list */}
                      <Box
                        component="ul"
                        sx={{
                          listStyle: "none",
                          p: 0,
                          m: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          minHeight: `${maxProcedures * 1.6}rem`, // Set minimum height based on max procedures
                        }}
                      >
                        {service.procedures?.map((procedure, idx) => (
                          <Box
                            component="li"
                            key={idx}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                            }}
                          >
                            <Box
                              sx={{
                                color: "primary.main",
                                mr: 1,
                                fontSize: "0.7rem",
                                mt: "5px",
                              }}
                            >
                              •
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: "0.85rem",
                                lineHeight: 1.4,
                              }}
                            >
                              {procedure}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Services;
